import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X, Search, ChevronDown, LogIn, Heart, Phone } from 'lucide-react'
import { PRIMARY_NAV } from './nav'
import { LanguageSwitcher } from './LanguageSwitcher'
import { SearchAutocomplete } from './SearchAutocomplete'
import { useModalA11y } from './useModalA11y'
import { useAuthStore } from '../../stores/authStore'
import type { Suggestion } from '../../services/searchService'

/** Brand mark — Ashoka-blue wordmark with a simple ribbon glyph. */
function Brand({ onClick }: { onClick?: () => void }) {
  const { t } = useTranslation()
  return (
    <Link to="/" onClick={onClick} className="flex items-center gap-2.5 shrink-0" aria-label={t('header.brandAria', 'TB Mukt Bharat home')}>
      <span className="w-9 h-9 rounded-xl bg-gradient-to-br from-[rgb(15_76_129)] to-[rgb(0_180_216)] flex items-center justify-center text-white font-extrabold shadow-md" aria-hidden="true">
        त
      </span>
      <span className="leading-tight min-w-0">
        <span className="block font-extrabold text-snow-900 text-sm sm:text-lg">TB Mukt Bharat</span>
        <span className="hidden sm:block text-[10px] sm:text-xs text-snow-500 -mt-0.5">{t('common.tagline', 'Together We Can End TB')}</span>
      </span>
    </Link>
  )
}

export function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQ, setSearchQ] = useState('')
  const dropRef = useRef<HTMLDivElement>(null)
  const drawerRef = useRef<HTMLDivElement>(null)
  // Keyboard a11y for the mobile drawer: focus in, Tab trap, Escape, focus restore.
  useModalA11y(mobileOpen, () => setMobileOpen(false), drawerRef)

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false)
    setOpenDropdown(null)
    setSearchOpen(false)
  }, [location.pathname])

  // Close dropdown on outside click
  useEffect(() => {
    if (!openDropdown) return
    const onDown = (e: MouseEvent) => {
      if (!dropRef.current?.contains(e.target as Node)) setOpenDropdown(null)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [openDropdown])

  const isActive = (path: string) =>
    location.pathname === path || (path !== '/' && location.pathname.startsWith(path))

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const q = searchQ.trim()
    setSearchOpen(false)
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search')
  }

  // Picking a suggestion (curated topic or live content) navigates to its path.
  const pickSuggestion = (s: Suggestion) => {
    setSearchOpen(false)
    setSearchQ('')
    navigate(s.path)
  }

  // Submitting raw typed text (no highlighted suggestion) runs a full search.
  const submitQuery = (q: string) => {
    setSearchOpen(false)
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search')
  }

  const loginLabel = t('nav.login', 'Login')
  const dashboardLabel = isAuthenticated
    ? user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'
      ? t('header.admin', 'Admin')
      : t('header.dashboard', 'Dashboard')
    : loginLabel
  const dashboardPath = isAuthenticated
    ? user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN'
      ? '/admin/dashboard'
      : '/dashboard'
    : '/login'

  return (
    <>
      {/* Skip link — first focusable element, becomes visible on focus */}
      <a href="#main-content" className="skip-link">
        {t('header.skipToContent', 'Skip to main content')}
      </a>

      {/* Top utility bar — helpline (action-critical) left; contact + language right.
          Compact 36px (h-9). Language lives here (not the main bar) so the main nav
          row has room to breathe and never wraps. */}
      <div className="bg-warm-50 text-snow-700 text-xs border-b border-warm-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 flex items-center justify-between h-9">
          <a href="tel:1800116666" className="flex items-center gap-1.5 text-human-700 hover:text-human-800 whitespace-nowrap" aria-label={t('header.helplineAria', 'TB helpline 1800 11 6666')}>
            <Phone className="w-3.5 h-3.5" aria-hidden="true" />
            <span>{t('header.helpline', 'Helpline: 1800-11-6666 (verified)')}</span>
          </a>
          <div className="flex items-center gap-3">
            <Link to="/contact" className="hover:text-human-700 hidden sm:inline whitespace-nowrap">
              {t('header.contact', 'Contact')}
            </Link>
            <LanguageSwitcher variant="bar" />
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-snow-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-2">
            <Brand />

            {/* Desktop nav — single row, NEVER wraps. Shown at xl+ where 8 grouped
                items + brand + actions all fit comfortably. Below xl → hamburger drawer.
                whitespace-nowrap on every label so "Find a TB Centre" etc. stay one line. */}
            <nav aria-label="Primary" className="hidden xl:flex items-center gap-1" ref={dropRef}>
              {PRIMARY_NAV.filter((item) => !item.mobileOnly).map((item) => (
                <div key={item.key} className="relative">
                  {item.children ? (
                    <>
                      <button
                        type="button"
                        aria-haspopup="true"
                        aria-expanded={openDropdown === item.key}
                        onClick={() => setOpenDropdown(openDropdown === item.key ? null : item.key)}
                        className={`flex items-center gap-1 px-2.5 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                          isActive(item.path) ? 'text-[rgb(15_76_129)] bg-[rgb(232_241_252)]' : 'text-snow-700 hover:bg-snow-100'
                        }`}
                      >
                        {t(`nav.${item.key}`, item.label)}
                        <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
                      </button>
                      {openDropdown === item.key && (
                        <div className="absolute left-0 top-full pt-2 w-60 animate-scale-in origin-top-left">
                          <div className="rounded-xl bg-white shadow-xl border border-snow-200 py-2">
                            {item.children.map((c) => (
                              <Link
                                key={c.path}
                                to={c.path}
                                className="flex flex-col px-4 py-2.5 hover:bg-snow-50 whitespace-nowrap"
                              >
                                <span className="text-sm font-medium text-snow-800">{t(`nav.${c.key}`, c.label)}</span>
                                {c.desc && <span className="text-xs text-snow-500">{t(`header.${c.key}Desc`, c.desc)}</span>}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      aria-current={isActive(item.path) ? 'page' : undefined}
                      className={`px-2.5 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                        isActive(item.path) ? 'text-[rgb(15_76_129)] bg-[rgb(232_241_252)]' : 'text-snow-700 hover:bg-snow-100'
                      }`}
                    >
                      {t(`nav.${item.key}`, item.label)}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Right actions — search + login on desktop; hamburger below xl.
                Language lives in the utility bar above, so it is not repeated here. */}
            <div className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => setSearchOpen((s) => !s)}
                aria-label={t('header.searchAria', 'Search')}
                aria-expanded={searchOpen}
                className="p-2 rounded-lg text-snow-600 hover:bg-snow-100 transition min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <Search className="w-5 h-5" aria-hidden="true" />
              </button>
              <Link
                to={dashboardPath}
                className="hidden sm:inline-flex items-center gap-1.5 ml-1 btn-primary btn-sm whitespace-nowrap"
              >
                {isAuthenticated ? (
                  <>
                    <Heart className="w-4 h-4" aria-hidden="true" />
                    {dashboardLabel}
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" aria-hidden="true" />
                    {loginLabel}
                  </>
                )}
              </Link>

              {/* Mobile menu toggle — shown below xl (mobile + tablet) */}
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                aria-label={t('header.openMenu', 'Open menu')}
                aria-expanded={mobileOpen}
                className="xl:hidden p-2 rounded-lg text-snow-700 hover:bg-snow-100 transition min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <Menu className="w-6 h-6" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Search bar (expandable, with autocomplete) */}
          {searchOpen && (
            <div className="pb-3 animate-fade-down">
              <form onSubmit={submitSearch} role="search" className="relative">
                <SearchAutocomplete
                  value={searchQ}
                  onChange={setSearchQ}
                  onSelectSuggestion={pickSuggestion}
                  onSubmit={submitQuery}
                  inputId="header-search"
                  autoFocus
                  srLabel={t('header.searchSrLabel', 'Search TB Mukt Bharat')}
                  placeholder={t('header.searchPlaceholder', 'Search TB information, resources, guidelines…')}
                  inputClassName="input pl-11"
                  leadingIcon={
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-snow-400 pointer-events-none" aria-hidden="true" />
                  }
                />
                <button type="submit" className="sr-only">
                  {t('header.searchAria', 'Search')}
                </button>
              </form>
              <p className="text-xs text-snow-500 mt-1.5 px-1">
                {t('header.searchHint', 'Search covers pages, knowledge articles, resources and FAQs.')}
              </p>
            </div>
          )}
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} aria-hidden="true" />
          <div ref={drawerRef} tabIndex={-1} className="relative w-80 max-w-[85vw] bg-white h-full overflow-y-auto shadow-2xl animate-slide-in-right outline-none">
            <div className="flex items-center justify-between p-4 border-b border-snow-200">
              <Brand onClick={() => setMobileOpen(false)} />
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label={t('header.closeMenu', 'Close menu')}
                className="p-2 rounded-lg hover:bg-snow-100 min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <X className="w-6 h-6" aria-hidden="true" />
              </button>
            </div>
            <nav aria-label="Mobile" className="p-3">
              {PRIMARY_NAV.map((item) => (
                <div key={item.key}>
                  <Link
                    to={item.path}
                    className={`block px-4 py-3 rounded-lg text-sm font-medium min-h-[44px] flex items-center ${
                      isActive(item.path) ? 'text-[rgb(15_76_129)] bg-[rgb(232_241_252)]' : 'text-snow-700 hover:bg-snow-50'
                    }`}
                  >
                    {t(`nav.${item.key}`, item.label)}
                  </Link>
                  {item.children && (
                    <div className="ml-3 border-l border-snow-200 pl-1 mb-1">
                      {item.children.map((c) => (
                        <Link
                          key={c.path}
                          to={c.path}
                          className="block px-4 py-2.5 rounded-lg text-sm text-snow-500 hover:bg-snow-50 min-h-[40px]"
                        >
                          {t(`nav.${c.key}`, c.label)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
            <div className="p-4 border-t border-snow-200 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-snow-600">{t('header.language', 'Language')}</span>
                <LanguageSwitcher />
              </div>
              <Link to={dashboardPath} className="btn-primary w-full justify-center">
                {isAuthenticated ? dashboardLabel : loginLabel}
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  )
}